import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { api } from '../lib/api';
import { auftraegeApi, type AuftragTyp } from '../lib/auftraege-api';
import { requireAccess } from '../lib/guards';

export const Route = createFileRoute('/auftraege/neu')({
  beforeLoad: () => requireAccess('/api/v1/auftraege'),
  component: NeuerAuftragPage,
});

const TYP_OPTIONS: Array<{ value: AuftragTyp; label: string }> = [
  { value: 'reparatur', label: 'Reparatur' },
  { value: 'wartung', label: 'Wartung' },
  { value: 'notdienst', label: 'Notdienst' },
  { value: 'kontrollgang', label: 'Kontrollgang' },
  { value: 'beratung', label: 'Beratung' },
  { value: 'reinigung', label: 'Reinigung' },
  { value: 'garten', label: 'Garten' },
  { value: 'sonstige', label: 'Sonstige' },
];

interface KundenListItem {
  id: string;
  kundennummer: string | null;
  firmenname: string;
}

function NeuerAuftragPage(): JSX.Element {
  const navigate = useNavigate();
  const [kundeId, setKundeId] = useState('');
  const [titel, setTitel] = useState('');
  const [typ, setTyp] = useState<AuftragTyp>('reparatur');
  const [beschreibung, setBeschreibung] = useState('');

  const kundenQ = useQuery({
    queryKey: ['kunden', 'list'],
    queryFn: () => api<{ items: KundenListItem[] }>('/api/v1/kunden'),
  });

  const create = useMutation({
    mutationFn: () =>
      auftraegeApi.create({
        kundeId,
        titel: titel.trim(),
        typ,
        ...(beschreibung.trim().length > 0 ? { beschreibung: beschreibung.trim() } : {}),
      }),
    onSuccess: ({ item }) => {
      void navigate({ to: '/auftraege/$id', params: { id: item.id } });
    },
  });

  const canSubmit = kundeId.length > 0 && titel.trim().length > 0 && !create.isPending;

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader
        title="Neuer Auftrag"
        description="Stamm-Daten anlegen — Status startet auf Entwurf."
      />

      {create.error ? <ErrorBanner error={create.error as Error} /> : null}

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) create.mutate();
        }}
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Kunde</span>
          <Select value={kundeId} onChange={(e) => setKundeId(e.target.value)} required>
            <option value="" disabled>
              – Kunde wählen –
            </option>
            {(kundenQ.data?.items ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.kundennummer ? `${k.kundennummer} · ` : ''}
                {k.firmenname}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Titel</span>
          <Input
            value={titel}
            onChange={(e) => setTitel(e.target.value)}
            maxLength={200}
            required
            placeholder="z. B. Heizung Wartung Hauptstr. 12"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Typ</span>
          <Select value={typ} onChange={(e) => setTyp(e.target.value as AuftragTyp)}>
            {TYP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Beschreibung (optional)
          </span>
          <textarea
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            rows={4}
            value={beschreibung}
            onChange={(e) => setBeschreibung(e.target.value)}
          />
        </label>

        <div className="flex gap-3">
          <Button type="submit" disabled={!canSubmit}>
            {create.isPending ? 'Speichere…' : 'Anlegen'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => void navigate({ to: '/auftraege' })}>
            Abbrechen
          </Button>
        </div>
      </form>
    </div>
  );
}
